import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { DealCard } from '../components/DealCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Search, Sparkles, Loader2, Send, Dog, History,
  Lightbulb, TrendingDown, ArrowRight, Zap, X, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const QUICK_SEARCHES = [
  { label: 'Najlepsze promocje na elektronikę', query: 'najlepsze promocje na elektronikę, laptopy, smartfony', icon: Zap },
  { label: 'Prezent do 100 zł', query: 'pomysł na prezent do 100 złotych', icon: Lightbulb },
  { label: 'Tanie podróże', query: 'tanie loty, hotele, wycieczki', icon: TrendingDown },
  { label: 'Moda poniżej 50 zł', query: 'ubrania, buty, moda poniżej 50 złotych', icon: TrendingDown },
];

const CATEGORIES = [
  { id: 'all', name: 'Wszystkie' },
  { id: 'elektronika', name: 'Elektronika' },
  { id: 'moda', name: 'Moda' },
  { id: 'dom', name: 'Dom' },
  { id: 'sport', name: 'Sport' },
  { id: 'jedzenie', name: 'Jedzenie' },
  { id: 'rozrywka', name: 'Rozrywka' },
  { id: 'podroze', name: 'Podróże' },
  { id: 'inne', name: 'Inne' },
];

export default function WachalskiPage() {
  const { authHeaders, token } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (token) fetchHistory();
  }, [token]);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/ai/search-history`, { headers: authHeaders() });
      setHistory(res.data.history);
    } catch {}
  };

  const handleSearch = async (searchQuery = null) => {
    const q = searchQuery || query;
    if (!q.trim()) { toast.error('Napisz czego szukasz!'); return; }
    if (!token) { toast.error('Zaloguj się aby korzystać z Wąchalskiego'); navigate('/auth'); return; }

    setLoading(true);
    setResult(null);
    setShowHistory(false);

    try {
      const res = await axios.post(`${API}/ai/search`, {
        query: q,
        budget_max: budgetMax ? parseFloat(budgetMax) : null,
        category: category !== 'all' ? category : null
      }, { headers: authHeaders() });
      setResult(res.data);
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wąchalski napotkał problem');
    }
    setLoading(false);
  };

  const handleReact = async (dealId, type) => {
    if (!token) return;
    try {
      await axios.post(`${API}/deals/${dealId}/react`, { type }, { headers: authHeaders() });
      // Refresh results
      if (result) {
        handleSearch(query);
      }
    } catch {}
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-20 md:pb-6" data-testid="wachalski-page">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-full bg-primary/20 border-2 border-primary flex items-center justify-center">
            <Dog className="w-7 h-7 text-primary" strokeWidth={2} />
          </div>
          <div className="text-left">
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
              WĄCHALSKI
            </h1>
            <p className="text-xs text-muted-foreground">AI Łowca Okazji</p>
          </div>
        </div>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Powiedz mi czego szukasz, a ja przeszukam wszystkie okazje i znajdę najlepsze dla Ciebie!
        </p>
      </div>

      {/* Search Form */}
      <div className="bg-card border border-border/40 p-4 mb-4" data-testid="wachalski-search-form">
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            ref={inputRef}
            data-testid="wachalski-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            onFocus={() => history.length > 0 && setShowHistory(true)}
            placeholder="Np. szukam laptopa do pracy do 3000 zł, albo prezent dla dziewczyny..."
            className="pl-11 pr-12 bg-input border-transparent focus:border-primary rounded-none h-12 text-base"
          />
          <Button
            data-testid="wachalski-search-btn"
            onClick={() => handleSearch()}
            disabled={loading}
            className="absolute right-1 top-1/2 -translate-y-1/2 bg-primary text-primary-foreground rounded-none h-10 w-10 p-0"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>

        {/* Filters row */}
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Label className="text-xs text-muted-foreground mb-1 block">Kategoria</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="wachalski-category" className="bg-input border-transparent rounded-none h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-32">
            <Label className="text-xs text-muted-foreground mb-1 block">Budżet max (zł)</Label>
            <Input
              data-testid="wachalski-budget"
              type="number"
              value={budgetMax}
              onChange={(e) => setBudgetMax(e.target.value)}
              placeholder="np. 500"
              className="bg-input border-transparent rounded-none h-9 text-xs"
            />
          </div>
        </div>

        {/* Search history dropdown */}
        <AnimatePresence>
          {showHistory && history.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className="mt-2 bg-popover border border-border/40 max-h-40 overflow-y-auto"
              data-testid="search-history-dropdown"
            >
              <div className="flex items-center justify-between px-3 py-2 border-b border-border/20">
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <History className="w-3 h-3" /> Ostatnie wyszukiwania
                </span>
                <button onClick={() => setShowHistory(false)} className="text-muted-foreground hover:text-white">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              {history.slice(0, 5).map((h, i) => (
                <button
                  key={i}
                  onClick={() => { setQuery(h.query); setShowHistory(false); handleSearch(h.query); }}
                  className="w-full text-left px-3 py-2 text-sm text-muted-foreground hover:text-white hover:bg-secondary/50 flex items-center gap-2 transition-colors"
                  data-testid={`history-item-${i}`}
                >
                  <Clock className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{h.query}</span>
                  <span className="text-xs text-muted-foreground/50 ml-auto flex-shrink-0">{h.results_count} wyników</span>
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Quick searches */}
      {!result && !loading && (
        <div className="mb-6" data-testid="quick-searches">
          <p className="text-xs text-muted-foreground mb-2 font-heading uppercase tracking-wider">Szybkie wyszukiwania</p>
          <div className="grid grid-cols-2 gap-2">
            {QUICK_SEARCHES.map(({ label, query: q, icon: Icon }) => (
              <button
                key={label}
                data-testid={`quick-search-${label.slice(0, 15)}`}
                onClick={() => { setQuery(q); handleSearch(q); }}
                className="flex items-center gap-2 p-3 bg-card border border-border/40 hover:border-primary/30 text-left transition-colors group"
              >
                <Icon className="w-4 h-4 text-primary flex-shrink-0 group-hover:scale-110 transition-transform" />
                <span className="text-xs text-muted-foreground group-hover:text-white transition-colors">{label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Loading animation */}
      {loading && (
        <div className="text-center py-12" data-testid="wachalski-loading">
          <motion.div
            animate={{ rotate: [0, 15, -15, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="inline-block mb-4"
          >
            <Dog className="w-16 h-16 text-primary" strokeWidth={1.5} />
          </motion.div>
          <p className="font-heading text-lg text-white">Wąchalski szuka...</p>
          <p className="text-sm text-muted-foreground">Przeszukuję bazę okazji z pomocą AI</p>
        </div>
      )}

      {/* Results */}
      {result && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="wachalski-results"
        >
          {/* AI Summary */}
          <div className="bg-card border border-primary/30 p-4 mb-4" data-testid="ai-summary">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Dog className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-white leading-relaxed">{result.message || result.ai_summary}</p>
                {result.tip && (
                  <div className="mt-2 flex items-start gap-2 text-xs text-zorki/80 bg-zorki/5 border border-zorki/20 p-2">
                    <Lightbulb className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>{result.tip}</span>
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  Przeszukano {result.total_searched} okazji | Znaleziono {result.deals?.length || 0} dopasowań
                </p>
              </div>
            </div>
          </div>

          {/* Matched deals */}
          {result.deals && result.deals.length > 0 ? (
            <div className="space-y-3">
              {result.deals.map((deal, i) => (
                <div key={deal.id} className="relative">
                  {/* AI Match reason badge */}
                  {deal.ai_match_reason && (
                    <div className="bg-primary/10 border-x border-t border-primary/20 px-3 py-1.5 flex items-center gap-2">
                      <Sparkles className="w-3 h-3 text-primary flex-shrink-0" />
                      <span className="text-xs text-primary/90">{deal.ai_match_reason}</span>
                    </div>
                  )}
                  <DealCard deal={deal} onReact={handleReact} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Dog className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">Wąchalski nie znalazł idealnych dopasowań.</p>
              <p className="text-sm text-muted-foreground mt-1">Spróbuj opisać inaczej lub zmień filtry.</p>
            </div>
          )}

          {/* New search */}
          <div className="mt-6 text-center">
            <Button
              onClick={() => { setResult(null); setQuery(''); inputRef.current?.focus(); }}
              className="bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-none"
              data-testid="new-search-btn"
            >
              <Search className="w-4 h-4 mr-2" /> Nowe wyszukiwanie
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
